import { defineStore } from 'pinia';
import { getAllModules } from '@/services/admin/module.service';
import { createStorageHandler, StorageType } from '@/utils/storage.utils';

/**
 * Module name→ID cache in sessionStorage.
 *
 * Cache lifecycle:
 * - Populated after first successful API fetch (once per session)
 * - Used for instant module_id resolution on page refresh (avoids race condition)
 * - Cleared on signout (resetModuleStore) and when the browser tab closes (sessionStorage)
 *
 * Adding a new module:
 *   No manual cache invalidation needed. SessionStorage clears on tab close,
 *   and resetModuleStore() clears it on signout. Users pick up new modules
 *   on their next login or new tab.
 */
const moduleMapStorage = createStorageHandler<Record<string, string>>('moduleMap', StorageType.SessionStorage);

// Shared promise so concurrent callers all await the same in-flight fetch
let pendingFetch: Promise<void> | null = null;

type ModuleStoreState = {
  moduleNameToIdMap: Record<string, string>;
  isLoaded: boolean;
};

export const useModuleStore = defineStore('module-store', {
  state: (): ModuleStoreState => {
    const cached = moduleMapStorage.get();
    return {
      moduleNameToIdMap: cached ?? {},
      isLoaded: !!cached,
    };
  },

  actions: {
    /**
     * Ensures module map is available for tracking.
     * Fetches once per session — subsequent calls return immediately.
     * On page refresh, sessionStorage cache provides instant availability.
     */
    async fetchModules(): Promise<void> {
      if (this.isLoaded) return;
      if (pendingFetch !== null) return pendingFetch;

      pendingFetch = (async (): Promise<void> => {
        try {
          const modules = await getAllModules();
          const map: Record<string, string> = {};
          for (const mod of modules) {
            map[mod.name] = mod.id;
          }
          this.moduleNameToIdMap = map;
          moduleMapStorage.set(map);
          this.isLoaded = true;
        } catch {
          // Silent fail - tracking lookup must never break UX
        } finally {
          pendingFetch = null;
        }
      })();

      return pendingFetch;
    },

    getModuleIdByName(moduleName: string): string | null {
      return this.moduleNameToIdMap[moduleName] ?? null;
    },

    resetModuleStore(): void {
      this.moduleNameToIdMap = {};
      this.isLoaded = false;
      moduleMapStorage.remove();
      pendingFetch = null;
    },
  },
});
